import { ContentModuleStatus, type GetContentModuleResponse } from '@/services/api/api.types'

export interface CMTableEntry {
  title: string
  filterFn: (cm: GetContentModuleResponse, isMyModule: boolean) => boolean
  displayColumnDefOptions: Record<string, unknown>
  toolbarActionsInclude?: Array<'new'>
}

const isHelpModule = (cm: GetContentModuleResponse): boolean => {
  return (cm.visibleToMsk && cm.allowedForAllSponsors) ?? false
}

export const sponsorAdminTables: CMTableEntry[] = [
  {
    title: 'All Modules',
    filterFn: (cm, _) => !isHelpModule(cm),
    displayColumnDefOptions: { 'mrt-row-actions': { size: 98 } },
    toolbarActionsInclude: ['new'],
  },
  {
    title: 'My Modules',
    filterFn: (_, isMyModule) => isMyModule,
    displayColumnDefOptions: { 'mrt-row-actions': { size: 98 } },
    toolbarActionsInclude: ['new'],
  },
  {
    title: 'Awaiting Approval',
    filterFn: (cm, _) => cm.status === 'pending_approval', // TODO: Add "under review" status when it exists
    displayColumnDefOptions: { 'mrt-row-actions': { size: 60 } },
    toolbarActionsInclude: ['new'],
  },
  {
    title: 'Published',
    filterFn: (cm, _) => cm.status === 'published' && !isHelpModule(cm),
    displayColumnDefOptions: { 'mrt-row-actions': { size: 60 } },
    toolbarActionsInclude: ['new'],
  },
  {
    title: 'In Progress',
    filterFn: (cm, _) => cm.status === 'testing' || cm.status === 'draft' || cm.status === 'pending_approval',
    displayColumnDefOptions: { 'mrt-row-actions': { size: 98 } },
    toolbarActionsInclude: ['new'],
  },
  {
    title: 'Help Modules',
    filterFn: (cm, _) => isHelpModule(cm),
    displayColumnDefOptions: { 'mrt-row-actions': { size: 60 } },
    toolbarActionsInclude: ['new'],
  },
]

export const smeTables: CMTableEntry[] = [
  {
    title: 'My Modules',
    filterFn: (_, isMyModule) => isMyModule,
    displayColumnDefOptions: { 'mrt-row-actions': { size: 98 } },
    toolbarActionsInclude: ['new'],
  },
  {
    title: 'All Modules',
    filterFn: (cm, _) => !isHelpModule(cm),
    displayColumnDefOptions: { 'mrt-row-actions': { size: 98 } },
    toolbarActionsInclude: ['new'],
  },
]

// Compute Table Permissions
// --------------------------------------------------------------------------------

export const canSponsorAdminEdit = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
  isVisibleToMsk: boolean,
  isEditable: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  if (isVisibleToMsk && (status === ContentModuleStatus.Draft || status === ContentModuleStatus.Testing)) {
    return true
  }

  return isMyModule && isEditable
}

export const canSponsorAdminDelete = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
  isVisibleToMsk: boolean,
  sponsorMskCms: GetContentModuleResponse[],
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  if (isVisibleToMsk && !isAllSponsorsModule) {
    // If this is a Sponsor MSK CM, makes sure it's not the last one
    if (sponsorMskCms.length === 1) {
      return false
    }
  }

  const invalidOptionsForSmeModules: ContentModuleStatus[] = [ContentModuleStatus.Removed]

  const invalidOptionsForMyModule = [ContentModuleStatus.Removed]

  return isMyModule || isVisibleToMsk
    ? !invalidOptionsForMyModule.includes(status)
    : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminPublish = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
  isVisibleToMsk: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  const invalidOptionsForSmeModules: ContentModuleStatus[] = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Draft,
    ContentModuleStatus.Testing,
    ContentModuleStatus.Published,
  ]

  const invalidOptionsForMyModule = [ContentModuleStatus.Removed, ContentModuleStatus.Published]

  return isMyModule || isVisibleToMsk
    ? !invalidOptionsForMyModule.includes(status)
    : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminMoveToDraft = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
  isVisibleToMsk: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  const invalidOptionsForSmeModules: ContentModuleStatus[] = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Testing,
    ContentModuleStatus.Draft,
  ]

  const invalidOptionsForMyModule = [ContentModuleStatus.Removed, ContentModuleStatus.Draft]

  return isMyModule || isVisibleToMsk
    ? !invalidOptionsForMyModule.includes(status)
    : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminMoveToTesting = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
  isVisibleToMsk: boolean,
): boolean => {
  // FIXME:This is a temporary workaround to prevent customers from moving MSK CMs to testing
  // MSKs currently have a bug where TESTING/PENDING MSK modules cannot be tested in the APP.
  // SEE: https://sidekickwellness.atlassian.net/browse/C2-1133
  if (isVisibleToMsk || isAllSponsorsModule) {
    return false
  }

  // Sponsor Admin cannot move a SME module to testing
  const invalidOptionsForSmeModules: ContentModuleStatus[] = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Draft,
    ContentModuleStatus.Published,
    ContentModuleStatus.PendingApproval,
    ContentModuleStatus.Testing,
  ]

  const invalidOptionsForMyModule = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Published,
    ContentModuleStatus.PendingApproval,
    ContentModuleStatus.Testing,
  ]

  return isMyModule ? !invalidOptionsForMyModule.includes(status) : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminMoveToPendingApproval = (
  status: ContentModuleStatus,
  _isMyModule: boolean,
  isAllSponsorsModule: boolean,
  _isVisibleToMsk: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  return status === ContentModuleStatus.Published
}
